import React, { useState } from "react";
import {
  createSpecialistProfile,
  CreateSpecialistPayload,
} from "../../../api/specialists";

import { SpecialistProfileFormState } from "./state/SpecialistProfileTypes";

import {
  SpecialistProfileProvider,
  useSpecialistProfile,
} from "./state/SpecialistProfileContext";

import {
  validateBasicProfile,
  validateClinicalProfile,
  validateAccess,
  validateHospitalsProcedures,
  validateConditions,
  validateReferral,
} from "./state/SpecialistProfileValidation";

import SpecialistProfileTabs from "./SpecialistProfileTabs";
import TabIdentityClinic from "./tabs/TabIdentityClinic";
import TabClinicalProfile from "./tabs/TabClinicalProfile";
import TabAccessCoverage from "./tabs/TabAccessCoverage";
import TabHospitalsProcedures from "./tabs/TabHospitalsProcedures";

// ✅ NEW tab components
import TabConditionsInvestigations from "./tabs/TabConditionsInvestigations";
import TabReferralWaitTimes from "./tabs/TabReferralWaitTimes";

type SpecialistProfileWizardProps = {
  mode: "create" | "edit";
};

const tabs = [
  "Identity & Clinic",
  "Clinical Profile",
  "Access & Coverage",
  "Hospitals & Procedures",
  "Conditions & Investigations",
  "Referral & Wait Times",
];

const deriveAgeGroupForApi = (ageGroups: string[]): string | null => {
  if (!ageGroups || ageGroups.length === 0) return null;
  if (ageGroups.length === 1) return ageGroups[0];
  return "Mixed";
};

const toNumberArray = (arr: any): number[] => {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((id) => parseInt(String(id), 10))
    .filter((n) => !isNaN(n));
};

const normalizeInvestigationIds = (val: any): number[] => {
  if (!val) return [];

  if (typeof val === "string") {
    return val
      .split(",")
      .map((x) => parseInt(x.trim(), 10))
      .filter((n) => Number.isFinite(n) && n > 0);
  }

  if (Array.isArray(val)) {
    return val
      .map((x) => parseInt(String(x), 10))
      .filter((n) => Number.isFinite(n) && n > 0);
  }

  return [];
};

const parseOptionalInt = (val: any): number | null => {
  if (val === null || val === undefined) return null;

  if (typeof val === "number") {
    return Number.isFinite(val) ? val : null;
  }

  const s = String(val).trim();
  if (!s) return null;
  const n = parseInt(s, 10);
  return isNaN(n) ? null : n;
};

const normalizeAvailabilityFromBasicProfile = (basicProfile: any): string | null => {
  const raw =
    basicProfile?.availabilityStatus ??
    basicProfile?.availability ??
    basicProfile?.status ??
    basicProfile?.availability_text ??
    basicProfile?.availabilityLabel ??
    null;

  if (raw === null || raw === undefined) return null;

  const s = String(raw).trim();
  return s.length > 0 ? s : null;
};

const normalizeReferralMethodCodes = (referral: any): string[] => {
  const raw =
    referral?.referralMethodCodes ??
    referral?.referralMethodCode ??
    referral?.methods ??
    null;

  if (!raw) return [];

  if (Array.isArray(raw)) {
    return Array.from(
      new Set(
        raw
          .map((x) => String(x || "").trim().toUpperCase())
          .filter((x) => x.length > 0)
      )
    );
  }

  // accept a CSV string too, just in case
  if (typeof raw === "string") {
    return Array.from(
      new Set(
        raw
          .split(",")
          .map((x) => x.trim().toUpperCase())
          .filter((x) => x.length > 0)
      )
    );
  }

  return [];
};

const SpecialistProfileWizardInner: React.FC<SpecialistProfileWizardProps> = ({
  mode,
}) => {
  const {
    formState,
    setBasicProfileErrors,
    setClinicalProfileErrors,
    setAccessErrors,
    setHospitalsProceduresErrors,
    setReferralErrors,
  } = useSpecialistProfile();

  const [currentStep, setCurrentStep] = useState(0);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  const validateCurrentStep = (): boolean => {
    const state: SpecialistProfileFormState = formState;

    switch (currentStep) {
      case 0: {
        const errors = validateBasicProfile(state);
        setBasicProfileErrors(errors);
        return Object.keys(errors).length === 0;
      }
      case 1: {
        const errors = validateClinicalProfile(state);
        setClinicalProfileErrors(errors);
        return Object.keys(errors).length === 0;
      }
      case 2: {
        const errors = validateAccess(state);
        setAccessErrors(errors);
        return Object.keys(errors).length === 0;
      }
      case 3: {
        const errors = validateHospitalsProcedures(state);
        setHospitalsProceduresErrors(errors);
        return Object.keys(errors).length === 0;
      }
      // Tab #5 now includes investigations, but conditions validation still lives here
      case 4: {
        validateConditions(state);
        return true;
      }
      // Tab #6 is Referral & Wait Times
      case 5: {
        const errors = validateReferral(state);
        setReferralErrors(errors);
        return Object.keys(errors).length === 0;
      }
      default:
        return true;
    }
  };

  const goToStep = (index: number) => {
    if (index <= currentStep) setCurrentStep(index);
  };

  const handleNext = () => {
    const isValid = validateCurrentStep();
    if (!isValid) return;
    setCurrentStep((prev) => Math.min(prev + 1, tabs.length - 1));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    const isValid = validateCurrentStep();
    if (!isValid) return;

    setSubmitError(null);
    setSubmitSuccess(null);
    setSubmitLoading(true);

    try {
      const {
        basicProfile,
        clinicalProfile,
        access,
        hospitalsAndProcedures,
        conditions,
        referral,
      } = formState;

      const availabilityStatus = normalizeAvailabilityFromBasicProfile(basicProfile);

      // Wait time inputs may be stored under either naming convention in state
      const routineWaitRaw =
        (referral as any)?.routineWaitWeeks ?? (referral as any)?.routineWeeks ?? null;

      const urgentWaitRaw =
        (referral as any)?.urgentWaitWeeks ?? (referral as any)?.urgentWeeks ?? null;

      // ✅ NEW: referral method codes (array of strings)
      const referralMethodCodes = normalizeReferralMethodCodes(referral);

      const payload: CreateSpecialistPayload = {
        userId: parseInt(basicProfile.userId, 10),
        clinicId: parseInt(basicProfile.clinicId, 10),
        fullName: basicProfile.fullName.trim(),
        cpsNumber: basicProfile.cpsNumber.trim(),
        gender: basicProfile.gender || null,

        specialtyId: parseInt(clinicalProfile.specialtyId, 10),
        ageGroup: deriveAgeGroupForApi(clinicalProfile.ageGroups),

        acceptingNewReferrals: basicProfile.acceptingNewReferrals === "yes",

        availabilityStatus,

        languageIds: toNumberArray(access.languageIds),
        visitTypeIds: toNumberArray(access.visitTypeIds),
        coverageTypeIds: toNumberArray(access.coverageTypeIds),

        hospitalIds: toNumberArray(hospitalsAndProcedures.hospitalIds),
        procedureIds: toNumberArray(hospitalsAndProcedures.procedureIds),
        excludedProcedureIds: toNumberArray(
          hospitalsAndProcedures.excludedProcedureIds
        ),

        primaryConditionIds: toNumberArray(conditions.primaryConditionIds),
        excludedConditionIds: toNumberArray(conditions.excludedConditionIds),

        requiredInvestigationIds: normalizeInvestigationIds(
          (referral as any).requiredInvestigationIds
        ),

        // ✅ Wait times that backend writes into WaitTimeSnapshot
        routineWaitWeeks: parseOptionalInt(routineWaitRaw),
        urgentWaitWeeks: parseOptionalInt(urgentWaitRaw),

        // ✅ NEW: referral method mapping
        referralMethodCodes,

        otherNotes:
          typeof (referral as any).otherNotes === "string" && (referral as any).otherNotes.trim()
            ? (referral as any).otherNotes.trim()
            : null,
      };

      console.log("Submitting specialist profile payload:", payload);

      const response = await createSpecialistProfile(payload);

      const anyResponse = response as any;
      const nameFromResponse =
        anyResponse?.specialist?.FullName ||
        anyResponse?.specialist?.fullName ||
        anyResponse?.FullName ||
        anyResponse?.fullName ||
        basicProfile.fullName.trim();

      setSubmitSuccess(`Successfully saved specialist profile for ${nameFromResponse}`);
      console.log("Create specialist response:", response);
    } catch (err: any) {
      console.error("Error creating specialist profile:", err);
      setSubmitError(
        err?.message || "Unexpected error while creating specialist profile."
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <TabIdentityClinic />;
      case 1:
        return <TabClinicalProfile />;
      case 2:
        return <TabAccessCoverage />;
      case 3:
        return <TabHospitalsProcedures />;
      case 4:
        return <TabConditionsInvestigations />;
      case 5:
        return <TabReferralWaitTimes />;
      default:
        return null;
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <SpecialistProfileTabs
        tabs={tabs}
        currentStep={currentStep}
        onStepClick={goToStep}
      />

      <div style={{ marginBottom: 24 }}>{renderStepContent()}</div>

      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <button
          type="button"
          onClick={handleBack}
          disabled={currentStep === 0 || submitLoading}
        >
          Back
        </button>

        {currentStep < tabs.length - 1 ? (
          <button type="button" onClick={handleNext} disabled={submitLoading}>
            Next
          </button>
        ) : (
          <button type="button" onClick={handleSubmit} disabled={submitLoading}>
            {submitLoading ? "Saving..." : "Save Specialist Profile"}
          </button>
        )}
      </div>

      {submitError && (
        <div style={{ marginTop: 16, color: "red" }}>{submitError}</div>
      )}
      {submitSuccess && (
        <div style={{ marginTop: 16, color: "green" }}>{submitSuccess}</div>
      )}
    </div>
  );
};

const SpecialistProfileWizard: React.FC<SpecialistProfileWizardProps> = (props) => {
  return (
    <SpecialistProfileProvider>
      <SpecialistProfileWizardInner {...props} />
    </SpecialistProfileProvider>
  );
};

export default SpecialistProfileWizard;
